import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail } from "lucide-react";
import { api, errorMessage, formatDate } from "@/lib/api";

const STATUSES = ["new", "reviewing", "accepted", "declined"];
const badge = { new: "bg-sky-400/15 text-sky-300", reviewing: "bg-amber-400/15 text-amber-300", accepted: "bg-emerald-400/15 text-emerald-300", declined: "bg-rose-400/15 text-rose-300" };
const KIND_LABEL = { pitch: "Pitch", contact: "Contact", advertise: "Advertising", club: "Club suggestion" };
const KIND_BADGE = { pitch: "bg-[#053671] text-sky-200", contact: "bg-violet-400/15 text-violet-300", advertise: "bg-emerald-400/15 text-emerald-300", club: "bg-amber-400/15 text-amber-300" };

export default function ApplicationsAdmin() {
  const [apps, setApps] = useState([]);
  const [kind, setKind] = useState("all");
  const visible = kind === "all" ? apps : apps.filter((a) => (a.kind || "pitch") === kind);
  const load = () => api.get("/admin/applications").then((r) => setApps(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (a, status) => {
    try { await api.put(`/admin/applications/${a.id}`, { status }); load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete application from ${a.name}?`)) return;
    try { await api.delete(`/admin/applications/${a.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div data-testid="admin-applications">
      <p className="eyebrow mb-2">Inbox</p>
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mb-2">Applications & messages</h1>
      <div className="flex flex-wrap gap-2 mb-8" data-testid="applications-filter">
        {[["all", "All"], ["pitch", "Show pitches"], ["contact", "Contact"], ["advertise", "Advertising"], ["club", "Club suggestions"]].map(([k, label]) => (
          <button key={k} onClick={() => setKind(k)} className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${kind === k ? "bg-sky-400 text-[#070F1E] border-sky-400" : "border-white/15 text-slate-300 hover:bg-white/10"}`} data-testid={`applications-filter-${k}`}>{label} <span className="font-mono opacity-70">{k === "all" ? apps.length : apps.filter((a) => (a.kind || "pitch") === k).length}</span></button>
        ))}
      </div>
      {visible.length === 0 && <p className="text-slate-400 text-sm" data-testid="applications-empty">Nothing here yet. Pitches, contact messages and advertising enquiries submitted on the site will appear here.</p>}
      <div className="space-y-3" data-testid="applications-list">
        {visible.map((a, i) => (
          <div key={a.id} className="card-surface rounded-2xl p-5" data-testid={`application-row-${i}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-mono uppercase ${KIND_BADGE[a.kind || "pitch"]}`} data-testid={`application-kind-${i}`}>{KIND_LABEL[a.kind || "pitch"]}</span>
                  <p className="font-display font-bold text-lg truncate">{a.kind === "contact" ? a.subject || "No subject" : a.kind === "advertise" ? a.company || a.name : a.show_name || "Untitled show"}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-mono uppercase ${badge[a.status]}`} data-testid={`application-status-${i}`}>{a.status}</span>
                </div>
                <p className="text-sm text-slate-400">{a.name} · <a href={`mailto:${a.email}`} className="text-sky-400 hover:underline inline-flex items-center gap-1"><Mail size={12} />{a.email}</a>{a.category ? ` · ${a.category}` : ""}{a.budget ? ` · Budget: ${a.budget}` : ""} · {formatDate(a.created_at)}</p>
              </div>
              <div className="flex items-center gap-2">
                <select value={a.status} onChange={(e) => setStatus(a, e.target.value)} className="h-9 rounded-full bg-white/5 border border-white/10 px-3 text-xs" data-testid={`application-status-select-${i}`}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={() => remove(a)} className="p-2 text-slate-400 hover:text-rose-400" data-testid={`delete-application-${i}`}><Trash2 size={16} /></button>
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-300 whitespace-pre-line leading-relaxed">{a.pitch}</p>
            {a.links && <p className="mt-2 text-xs text-slate-400 break-all">Links: {a.links}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
